import { Constants, EffectRenderer, EffectWrapper, Mesh, RenderTargetTexture, ShaderMaterial, Texture, Vector3, Vector4, VertexData, type Scene } from "@babylonjs/core";
import { SHIP } from "@tiny-seas/shared";
import { SIM_HULLS, type HullSource } from "./water-sim.ts";

/**
 * Spray that comes out of the water simulation: droplets leave the simulated surface wherever it
 * jets upwards fast near a hull (bow waves climbing the stem, rings round a slamming bow), fly
 * ballistically under gravity and air drag, and fade. Particle state lives in float textures and
 * is advanced on the GPU; nothing is placed by hand.
 */

const SLOTS = 64;
const COUNT = SLOTS * SLOTS;

const VERTEX = `
precision highp float;
attribute vec2 position;
varying vec2 vUV;
void main(void) {
  vUV = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}`;

/** Shared by both state passes, so they agree on which dead slots spawn and where. */
const SPAWN = `
precision highp float;
// Positions are world metres: lowp samplers would read them at half precision.
precision highp sampler2D;
#define HULLS ${SIM_HULLS}
#define HALF_LENGTH ${(SHIP.length / 2).toFixed(2)}
#define HALF_BEAM ${(SHIP.beam / 2).toFixed(2)}
varying vec2 vUV;
uniform sampler2D surface;
// Simulation window (centre x, centre z, extent, dt); (seed, spawn rate, window shift x, z in m).
uniform vec4 window;
uniform vec4 frame;
// Per hull: (x, z, heading, speed) and the swell's plane under it (heave m, pitch, roll rad as ShipMotion).
uniform vec4 hulls[HULLS];
uniform vec4 planes[HULLS];
uniform float hullCount;
float hash(vec2 p, float salt) {
  vec3 p3 = fract(vec3(p.xyx + salt) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
// A dead slot samples a point beside a hull (mostly towards the bow); it spawns with a chance
// that grows with how fast the water there is rising.
bool spawn(out vec3 position, out vec3 velocity, out float life) {
  vec2 slot = floor(vUV * ${SLOTS.toFixed(1)});
  float pick = floor(hash(slot, frame.x) * hullCount);
  vec4 h = vec4(0.0), plane = vec4(0.0);
  for (int i = 0; i < HULLS; i++) if (float(i) == pick) { h = hulls[i]; plane = planes[i]; }
  vec2 f = vec2(sin(h.z), cos(h.z)), r = vec2(f.y, -f.x);
  float s = 1.15 - 2.3 * pow(hash(slot, frame.x + 1.7), 2.0), side = hash(slot, frame.x + 2.3) < 0.5 ? -1.0 : 1.0;
  float n = side * mix(0.75, 1.7, hash(slot, frame.x + 3.1)) * sqrt(max(0.05, 1.0 - min(s * s * s * s, 1.0)));
  vec2 p = h.xy + f * s * HALF_LENGTH + r * n * HALF_BEAM;
  vec2 uv = (p - window.xy) / window.z + 0.5;
  vec2 water = texture2D(surface, uv).xy;
  float rising = smoothstep(0.6, 2.5, water.y);
  bool born = pick < hullCount && hash(slot, frame.x + 4.9) < rising * frame.y * window.w;
  // The swell under a hull is near enough its plane there; the simulated hull waves ride on it.
  float swell = plane.x - sin(plane.y) * s * HALF_LENGTH + sin(plane.z) * n * HALF_BEAM;
  position = vec3(p.x - window.x, swell + water.x + 0.05, p.y - window.y);
  // The jet keeps the water's upward speed and is flung outward from the hull.
  float out1 = mix(1.0, 3.5, hash(slot, frame.x + 5.3));
  velocity = vec3(r * side * out1 + f * h.w * 0.35, 0.0).xzy;
  velocity.y = water.y * mix(1.1, 2.0, hash(slot, frame.x + 6.1));
  life = mix(0.6, 1.4, hash(slot, frame.x + 7.7));
  return born;
}`;

const VELOCITY = `${SPAWN}
uniform sampler2D particles;
uniform sampler2D motion;
void main(void) {
  vec4 p = texture2D(particles, vUV), v = texture2D(motion, vUV);
  if (p.w < v.w) {
    float dt = window.w;
    v.xyz *= exp(-0.8 * dt);
    v.y -= 9.81 * dt;
    gl_FragColor = v;
    return;
  }
  vec3 position, velocity;
  float life;
  gl_FragColor = spawn(position, velocity, life) ? vec4(velocity, life) : vec4(0.0);
}`;

const POSITION = `${SPAWN}
uniform sampler2D particles;
uniform sampler2D motion;
uniform sampler2D motionNext;
void main(void) {
  vec4 p = texture2D(particles, vUV), v = texture2D(motion, vUV), next = texture2D(motionNext, vUV);
  if (p.w < v.w) {
    p.xyz += next.xyz * window.w - vec3(frame.z, 0.0, frame.w);
    p.w += window.w;
    gl_FragColor = p;
    return;
  }
  vec3 position, velocity;
  float life;
  gl_FragColor = spawn(position, velocity, life) ? vec4(position, 0.0) : vec4(0.0, -100.0, 0.0, 1.0);
}`;

/** Camera-facing streaks stretched along each droplet's flight; dead slots collapse off-screen. */
const DRAW_VS = `
precision highp float;
precision highp sampler2D;
attribute vec3 position;
attribute vec2 uv;
uniform mat4 viewProjection;
uniform sampler2D particles;
uniform sampler2D motion;
uniform vec4 window;
uniform vec3 cameraPosition;
varying vec2 vCorner;
varying float vFade;
void main(void) {
  vec4 p = texture2D(particles, uv), v = texture2D(motion, uv);
  if (p.w >= v.w || v.w <= 0.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vFade = 0.0; vCorner = vec2(0.0); return; }
  vec3 world = vec3(window.x + p.x, p.y, window.y + p.z);
  float t = p.w / v.w, speed = length(v.xyz);
  vec3 along = speed > 0.01 ? v.xyz / speed : vec3(0.0, 1.0, 0.0);
  vec3 across = normalize(cross(along, normalize(cameraPosition - world)) + vec3(1e-4, 0.0, 0.0));
  float size = mix(0.12, 0.5, t);
  world += across * position.x * size + along * position.y * size * (1.0 + speed * 0.12);
  vCorner = position.xy;
  vFade = smoothstep(0.0, 0.08, t) * (1.0 - t);
  gl_Position = viewProjection * vec4(world, 1.0);
}`;

const DRAW_FS = `
precision highp float;
uniform vec3 tint;
varying vec2 vCorner;
varying float vFade;
void main(void) {
  float disc = 1.0 - smoothstep(0.3, 1.0, length(vCorner));
  gl_FragColor = vec4(tint, disc * vFade * 0.75);
}`;

function stateTarget(scene: Scene, name: string, type: number): RenderTargetTexture {
  const target = new RenderTargetTexture(name, SLOTS, scene, { type, generateMipMaps: false, generateDepthBuffer: false, samplingMode: Texture.NEAREST_SAMPLINGMODE, gammaSpace: false });
  target.wrapU = target.wrapV = Texture.CLAMP_ADDRESSMODE;
  return target;
}

export class SeaSpray {
  readonly mesh: Mesh;
  private readonly material: ShaderMaterial;
  private readonly renderer: EffectRenderer;
  private readonly velocityPass: EffectWrapper;
  private readonly positionPass: EffectWrapper;
  private readonly particles: [RenderTargetTexture, RenderTargetTexture];
  private readonly motion: [RenderTargetTexture, RenderTargetTexture];
  private current = 0;
  private surface: Texture | null = null;
  private readonly window = [0, 0, 1, 0];
  private readonly frameArgs = [0, 0, 0, 0];
  private readonly hulls = new Array<number>(SIM_HULLS * 4).fill(0);
  private readonly planes = new Array<number>(SIM_HULLS * 4).fill(0);
  private hullCount = 0;
  private seed = 0;

  constructor(scene: Scene) {
    const engine = scene.getEngine();
    const type = engine.getCaps().textureFloatRender ? Constants.TEXTURETYPE_FLOAT : Constants.TEXTURETYPE_HALF_FLOAT;
    this.particles = [stateTarget(scene, "spray-particles-a", type), stateTarget(scene, "spray-particles-b", type)];
    this.motion = [stateTarget(scene, "spray-motion-a", type), stateTarget(scene, "spray-motion-b", type)];
    this.renderer = new EffectRenderer(engine);
    const uniforms = ["window", "frame", "hulls", "planes", "hullCount"];
    this.velocityPass = new EffectWrapper({ engine, name: "sprayVelocity", vertexShader: VERTEX, fragmentShader: VELOCITY, uniformNames: uniforms, samplerNames: ["surface", "particles", "motion"] });
    this.positionPass = new EffectWrapper({ engine, name: "sprayPosition", vertexShader: VERTEX, fragmentShader: POSITION, uniformNames: uniforms, samplerNames: ["surface", "particles", "motion", "motionNext"] });
    for (const wrapper of [this.velocityPass, this.positionPass]) {
      wrapper.onApplyObservable.add(() => {
        const e = wrapper.effect;
        e.setTexture("surface", this.surface);
        e.setTexture("particles", this.particles[this.current]!);
        e.setTexture("motion", this.motion[this.current]!);
        if (wrapper === this.positionPass) e.setTexture("motionNext", this.motion[1 - this.current]!);
        e.setFloat4("window", this.window[0]!, this.window[1]!, this.window[2]!, this.window[3]!);
        e.setFloat4("frame", this.frameArgs[0]!, this.frameArgs[1]!, this.frameArgs[2]!, this.frameArgs[3]!);
        e.setArray4("hulls", this.hulls);
        e.setArray4("planes", this.planes);
        e.setFloat("hullCount", this.hullCount);
      });
    }

    // One quad per slot: corners in `position`, the slot's state texel in `uv`.
    const positions: number[] = [], uvs: number[] = [], indices: number[] = [];
    for (let i = 0; i < COUNT; i++) {
      const u = (i % SLOTS + 0.5) / SLOTS, v = (Math.floor(i / SLOTS) + 0.5) / SLOTS, base = i * 4;
      for (const [cx, cy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
        positions.push(cx, cy, 0);
        uvs.push(u, v);
      }
      indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
    this.mesh = new Mesh("seaSpray", scene);
    const data = new VertexData();
    data.positions = positions;
    data.uvs = uvs;
    data.indices = indices;
    data.applyToMesh(this.mesh);
    this.mesh.alwaysSelectAsActiveMesh = true;
    this.mesh.isPickable = false;
    this.mesh.alphaIndex = 20;
    this.material = new ShaderMaterial("seaSprayMat", scene, { vertexSource: DRAW_VS, fragmentSource: DRAW_FS }, {
      attributes: ["position", "uv"], uniforms: ["viewProjection", "window", "cameraPosition", "tint"], samplers: ["particles", "motion"], needAlphaBlending: true,
    });
    this.material.backFaceCulling = false;
    this.material.disableDepthWrite = true;
    this.mesh.material = this.material;
  }

  /**
   * Advances every droplet by `dt` using the simulation's `surface` over (centreX, centreZ, extent),
   * whose window moved by (shiftX, shiftZ) metres this frame. `rate` scales spawning; 0 stops it.
   */
  update(dt: number, surface: Texture, centreX: number, centreZ: number, extent: number, shiftX: number, shiftZ: number,
    hulls: readonly HullSource[], rate: number, camera: Vector3, light: number): void {
    if (dt <= 0 || !this.velocityPass.effect.isReady() || !this.positionPass.effect.isReady()) return;
    this.surface = surface;
    this.window.splice(0, 4, centreX, centreZ, extent, Math.min(dt, 0.1));
    this.seed = (this.seed + 17.31) % 997;
    this.frameArgs.splice(0, 4, this.seed, rate * 40, shiftX, shiftZ);
    this.hullCount = Math.min(SIM_HULLS, hulls.length);
    for (let i = 0; i < this.hullCount; i++) {
      const h = hulls[i]!;
      this.hulls.splice(i * 4, 4, h.x, h.z, h.heading, Math.abs(h.speed));
      this.planes.splice(i * 4, 4, h.water.heave, h.water.pitch, h.water.roll, 0);
    }
    const next = 1 - this.current;
    this.renderer.render(this.velocityPass, this.motion[next]!);
    this.renderer.render(this.positionPass, this.particles[next]!);
    this.current = next;
    this.material.setTexture("particles", this.particles[this.current]!);
    this.material.setTexture("motion", this.motion[this.current]!);
    this.material.setVector4("window", new Vector4(centreX, centreZ, extent, 0));
    this.material.setVector3("cameraPosition", camera);
    this.material.setVector3("tint", new Vector3(light, light, light));
  }

  dispose(): void {
    for (const w of [this.velocityPass, this.positionPass]) w.dispose();
    this.renderer.dispose();
    for (const t of [...this.particles, ...this.motion]) t.dispose();
    this.mesh.dispose();
    this.material.dispose();
  }
}
